import React, { useState, useEffect } from 'react';
import Select from 'react-select';

const UserFilters = ({
  filters,
  dropdowns = {},
  onFilterChange,
  onClearFilters,
  filtersToShow, // optional array override
}) => {
  const visibleFilters =
    filtersToShow || [
      'clusterId',
      'chapterId',
      'cityId',
      'stateId',
      'businessCategoryId',
      'businessSubCategoryId',
      'startDate',
      'endDate',
    ];

  const clusters = dropdowns.clusters || [];
  const chapters = dropdowns.chapters || [];
  const cities = dropdowns.cities || [];
  const states = dropdowns.states || [];
  const businessCategories = dropdowns.businessCategories || [];
  const businessSubCategories = dropdowns.businessSubCategories || [];

  // Local date state so "Apply" controls when filters are applied
  const [localStartDate, setLocalStartDate] = useState(filters.startDate || '');
  const [localEndDate, setLocalEndDate] = useState(filters.endDate || '');
  const [dateFields, setDateFields] = useState({
    joiningDate: (filters.dateField || '').split(',').includes('joiningDate'),
    expirationDate: (filters.dateField || '').split(',').includes('expirationDate'),
  });

  // keep local state in sync when parent filters change externally
  useEffect(() => {
    setLocalStartDate(filters.startDate || '');
    setLocalEndDate(filters.endDate || '');
    setDateFields({
      joiningDate: (filters.dateField || '').split(',').includes('joiningDate'),
      expirationDate: (filters.dateField || '').split(',').includes('expirationDate'),
    });
  }, [filters.startDate, filters.endDate, filters.dateField]);

  const handleSingleChange = (key, selectedOption) => {
    const value = selectedOption ? selectedOption.value : '';
    onFilterChange(key, value);
  };

  const toggleDateField = (field) => {
    setDateFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const applyDateFilter = () => {
    // dateField can be comma separated if both selected
    const selectedFields = Object.keys(dateFields).filter((k) => dateFields[k]).join(',');
    onFilterChange('dateField', selectedFields);
    onFilterChange('startDate', localStartDate || '');
    onFilterChange('endDate', localEndDate || '');
  };

  const handleClearFilters = () => {
    // reset locals then call parent clear
    setLocalStartDate('');
    setLocalEndDate('');
    setDateFields({ joiningDate: false, expirationDate: false });
    onClearFilters();
  };

  return (
    <div className="d-flex flex-wrap gap-3 mb-3 align-items-end border p-3 rounded shadow-sm bg-white">
      {visibleFilters.includes('chapterId') && (
        <div style={{ minWidth: 180 }}>
          <label className="form-label small mb-1">Chapter</label>
          <Select
            options={chapters}
            value={chapters.find((opt) => opt.value === filters.chapterId) || null}
            onChange={(selected) => handleSingleChange('chapterId', selected)}
            placeholder="Select Chapter"
            isClearable
            classNamePrefix="react-select"
          />
        </div>
      )}

      {visibleFilters.includes('cityId') && (
        <div style={{ minWidth: 180 }}>
          <label className="form-label small mb-1">City</label>
          <Select
            options={cities}
            value={cities.find((opt) => opt.value === filters.cityId) || null}
            onChange={(selected) => handleSingleChange('cityId', selected)}
            placeholder="Select City"
            isClearable
            classNamePrefix="react-select"
          />
        </div>
      )}

      {visibleFilters.includes('stateId') && (
        <div style={{ minWidth: 180 }}>
          <label className="form-label small mb-1">State</label>
          <Select
            options={states}
            value={states.find((opt) => opt.value === filters.stateId) || null}
            onChange={(selected) => handleSingleChange('stateId', selected)}
            placeholder="Select State"
            isClearable
            classNamePrefix="react-select"
          />
        </div>
      )}

      {visibleFilters.includes('businessCategoryId') && (
        <div style={{ minWidth: 220 }}>
          <label className="form-label small mb-1">Business Category</label>
          <Select
            options={businessCategories}
            value={
              businessCategories.find((opt) => opt.value === filters.businessCategoryId) || null
            }
            onChange={(selected) => handleSingleChange('businessCategoryId', selected)}
            placeholder="Select Business Category"
            isClearable
            classNamePrefix="react-select"
          />
        </div>
      )}

      {visibleFilters.includes('businessSubCategoryId') && (
        <div style={{ minWidth: 240 }}>
          <label className="form-label small mb-1">Business Sub Category</label>
          <Select
            options={businessSubCategories}
            value={
              businessSubCategories.filter((opt) =>
                (filters.businessSubCategoryId || '').split(',').includes(opt.value)
              )
            }
            onChange={(selected) => {
              // Multi-select: selected is array
              const value = selected && selected.length
                ? selected.map((opt) => opt.value).join(',')
                : '';
              onFilterChange('businessSubCategoryId', value);
            }}
            placeholder="Select Business Sub Category"
            isClearable
            isMulti
            classNamePrefix="react-select"
          />
        </div>
      )}

      {(visibleFilters.includes('startDate') || visibleFilters.includes('endDate')) && (
        <div style={{ minWidth: 380 }}>
          <label className="form-label small mb-1">Date Range</label>
          <div className="d-flex gap-2 mb-2 align-items-center">
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="checkbox"
                id="filterJoiningDate"
                checked={dateFields.joiningDate}
                onChange={() => toggleDateField('joiningDate')}
              />
              <label className="form-check-label small" htmlFor="filterJoiningDate">Joining Date</label>
            </div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="checkbox"
                id="filterExpirationDate"
                checked={dateFields.expirationDate}
                onChange={() => toggleDateField('expirationDate')}
              />
              <label className="form-check-label small" htmlFor="filterExpirationDate">Expiration Date</label>
            </div>
          </div>
          <div className="d-flex gap-2">
            {visibleFilters.includes('startDate') && (
              <input
                type="date"
                className="form-control form-control-sm"
                value={localStartDate}
                onChange={(e) => setLocalStartDate(e.target.value)}
                style={{ flex: 1 }}
              />
            )}
            <span className="align-self-center" style={{ fontSize: 13 }}>
              to
            </span>
            {visibleFilters.includes('endDate') && (
              <input
                type="date"
                className="form-control form-control-sm"
                value={localEndDate}
                onChange={(e) => setLocalEndDate(e.target.value)}
                style={{ flex: 1 }}
              />
            )}
            <button type="button" className="btn btn-sm btn-success" onClick={applyDateFilter}>
              Apply
            </button>
          </div>
        </div>
      )}

      <div className="align-self-end">
        <button className="btn btn-sm btn-outline-danger mt-2" onClick={handleClearFilters}>
          Clear Filters
        </button>
      </div>
    </div>
  );
};

export default UserFilters;